/**
 * Plan carry-through (CEL-1234, Pattern A — ruled Aug 14 2026).
 *
 * /pricing plan buttons send logged-out visitors to /signup?plan=<TIER>&interval=<INTERVAL>.
 * Before this existed nothing consumed the param — the choice was silently dropped after
 * signup (30d data: 7 plan-carrying signups, 0 ever paid). The flow now:
 *
 *   1. SignUp stashes the choice in localStorage at mount. localStorage survives the
 *      Google OAuth round-trip (same mechanism as firstTouch.js), the popup-blocked
 *      signInWithRedirect fallback (CEL-1086), email verification, and consent.
 *   2. WelcomePage — the FIRST in-app screen every new account sees, right after
 *      consent — consumes the stash and takes the user straight to Stripe checkout
 *      for that plan, with a clear "skip" escape into the free product.
 *
 * Unlike firstTouch this is LAST-write-wins: the latest plan click is the current
 * intent. The TTL bounds staleness — long enough to survive a slow email-verification
 * detour, short enough that a stale choice doesn't ambush someone returning days later.
 */

const PLAN_INTENT_KEY = 'cellcog_plan_intent';
const PLAN_INTENT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Mirrors the backend tier set (py/cellcog/api/billing/billing.py CheckoutRequest).
// The backend re-validates at checkout — this list only decides whether a param is
// worth stashing. Unknown values (garbage, or a tier newer than this build) are
// dropped: the flow falls back to the normal no-plan signup, never an error.
const KNOWN_TIERS = ['STARTER', 'BASIC', 'PRO', 'PRO_2X', 'PRO_4X', 'PRO_8X', 'NITRO', 'NITRO_2X'];

/** Stash ?plan= (+ optional ?interval=) from the current URL. Call at signup-page mount. */
export function capturePlanIntent() {
  try {
    const params = new URLSearchParams(window.location.search);
    const plan = (params.get('plan') || '').toUpperCase();
    if (!KNOWN_TIERS.includes(plan)) return;
    const interval = (params.get('interval') || 'MONTHLY').toUpperCase();
    localStorage.setItem(
      PLAN_INTENT_KEY,
      JSON.stringify({
        plan,
        interval: interval === 'YEARLY' ? 'YEARLY' : 'MONTHLY',
        captured_at: Date.now(),
      })
    );
  } catch (e) {
    // Fail-soft: plan carry must never break signup (localStorage disabled, etc).
  }
}

/**
 * Consume the stashed intent (single-shot: read + delete). Returns
 * {plan, interval} or null when absent / expired / invalid.
 */
export function getAndClearPlanIntent() {
  try {
    const raw = localStorage.getItem(PLAN_INTENT_KEY);
    if (!raw) return null;
    localStorage.removeItem(PLAN_INTENT_KEY);
    const intent = JSON.parse(raw);
    if (!intent || !KNOWN_TIERS.includes(intent.plan)) return null;
    if (!intent.captured_at || Date.now() - intent.captured_at > PLAN_INTENT_TTL_MS) return null;
    return {
      plan: intent.plan,
      interval: intent.interval === 'YEARLY' ? 'YEARLY' : 'MONTHLY',
    };
  } catch (e) {
    return null;
  }
}

/**
 * Non-destructive peek: is a valid, unexpired plan intent stashed right now?
 * Used by the post-consent redirect (Aug 28 2026, CEL-1848): ordinary signups
 * skip /welcome and land directly on the fork; only carried-plan signups still
 * route through /welcome, which silently forwards them to Stripe checkout.
 * Mirrors getAndClearPlanIntent's validation exactly, without consuming.
 */
export function hasPlanIntent() {
  try {
    const raw = localStorage.getItem(PLAN_INTENT_KEY);
    if (!raw) return false;
    const intent = JSON.parse(raw);
    if (!intent || !KNOWN_TIERS.includes(intent.plan)) return false;
    if (!intent.captured_at || Date.now() - intent.captured_at > PLAN_INTENT_TTL_MS) return false;
    return true;
  } catch (e) {
    return false;
  }
}
